import {
  normalizeSearch,
  pickAuthoredText,
  planExerciseSlugs,
  planMuscles,
  programExerciseSlugs,
  programMuscles,
  type EquipmentKind,
  type Exercise,
  type Locale,
  type Plan,
  type Program,
  type ResolvedPlanTemplate,
  type ResolvedProgramTemplate,
} from "@heraion/shared";

import { favoriteKey } from "./useFavorites";

// Pure filter logic for the Library browser (HR-S3c) — same shape as the
// Catalog's computeCatalogSections: normalized-substring search, then
// OR-within-axis / AND-across-axes on the tag sets. An item that lacks an
// axis entirely (a plan has no per-week frequency, a personal row has no
// difficulty) simply doesn't match while that axis is active — the axis
// filters FOR the attribute, it doesn't grandfather unknowns in.

export type LibraryFilter =
  | "favorites"
  | "programs"
  | "workouts"
  | "personal"
  | "coach";

export type LibraryItem =
  | { key: string; kind: "program"; source: "personal"; program: Program }
  | { key: string; kind: "plan"; source: "personal"; plan: Plan }
  | {
      key: string;
      kind: "program";
      source: "official";
      template: ResolvedProgramTemplate;
    }
  | {
      key: string;
      kind: "plan";
      source: "official";
      template: ResolvedPlanTemplate;
    };

// PE-21: a program's name may carry several languages. `locale` is the
// reader's; omitted, the chain starts at the program's author.
export const libraryItemName = (
  item: LibraryItem,
  locale?: Locale,
): string => {
  switch (item.source) {
    case "personal":
      return item.kind === "program"
        ? (pickAuthoredText(
            item.program.name,
            locale,
            item.program.authorLocale,
          ) ?? "")
        : item.plan.name;
    case "official":
      return item.template.name;
  }
};

/** The FAVORITE#<kind>#<id> identity of an item: null when the item
 *  isn't favoritable (templates, until PE-8 makes them real rows). */
export const libraryFavoriteKey = (item: LibraryItem): string | null =>
  item.source === "personal"
    ? favoriteKey(
        item.kind,
        item.kind === "program" ? item.program.id : item.plan.id,
      )
    : null;

/** A program published BY Metria, as opposed to a row of your own.
 *  Its card carries a single action (Follow) and the copy that action
 *  makes is the mechanism underneath, never a gesture the athlete has to
 *  perform (archive/programs-follow-model.md §5 S5). Lives here, next to the other
 *  pure item predicates, so the clone status rule below and the card's
 *  label can't drift on what counts as official. */
export const isOfficialProgram = (
  item: LibraryItem,
): item is Extract<LibraryItem, { source: "official"; kind: "program" }> =>
  item.source === "official" && item.kind === "program";

/** A row the caller FOLLOWS: read-only consequence (no edit, status move,
 *  or delete). Named READ-ONLY, not "followed", on purpose: `onFollow` /
 *  `following` on this card mean something else (status axis for your own
 *  programs). Naming the consequence keeps them apart. */
export const isReadOnlyItem = (
  item: LibraryItem,
  followedKeys: ReadonlySet<string> = new Set(),
): boolean => {
  const key = libraryFavoriteKey(item);
  return key !== null && followedKeys.has(key);
};

/** The sub of whoever created a personal item (program or plan), or `null` for
 *  an official template. PE-48: exported to avoid hand-written duplication. */
export const personalCreatedBy = (item: LibraryItem): string | null =>
  item.source !== "personal"
    ? null
    : item.kind === "program"
      ? item.program.createdBy
      : item.plan.createdBy;

/** Items visible under one filter-tab. Pure. Shared by every mount.
 *  `callerSub` drives the "coach" tab and, when a view is set, the athlete
 *  view (`assignedTo` must contain it) and the coach view (own rows).
 *  ⛔ `null` means "not yet known": with a view set it shows NO personal
 *  rows, and the "coach" tab matches nothing. It defaults to null so
 *  callsites without a view need not thread it. */
export const filterLibraryItems = (
  items: ReadonlyArray<LibraryItem>,
  filter: LibraryFilter,
  favoriteKeys: ReadonlySet<string> = new Set(),
  callerSub: string | null = null,
  /** CO-19: the active view (Experience) this library mount is showing,
   *  `null` (the default) means no filtering. Personal rows only; official
   *  templates always pass.
   *  - athlete: shown iff assigned to `callerSub` (empty/missing hidden).
   *  - coach: the caller's own rows authored as "coach" (coaching.md §2). */
  authoredAs: Program["authoredAs"] | null = null,
): LibraryItem[] => {
  const matchesView = (i: LibraryItem): boolean => {
    if (authoredAs === null) return true;
    if (i.source !== "personal") return true;
    if (authoredAs === "athlete") {
      // `?? []`: hand-built rows may lack `assignedTo`; they stay hidden.
      const assignedTo =
        (i.kind === "program" ? i.program.assignedTo : i.plan.assignedTo) ??
        [];
      return callerSub !== null && assignedTo.includes(callerSub);
    }
    if (personalCreatedBy(i) !== callerSub) return false;
    const itemAuthoredAs =
      i.kind === "program" ? i.program.authoredAs : i.plan.authoredAs;
    return (itemAuthoredAs ?? "athlete") === authoredAs;
  };

  switch (filter) {
    case "favorites": {
      return items
        .filter((i) => {
          const key = libraryFavoriteKey(i);
          return key !== null && favoriteKeys.has(key);
        })
        .filter(matchesView);
    }
    case "programs":
      return items.filter((i) => i.kind === "program").filter(matchesView);
    case "workouts":
      return items.filter((i) => i.kind === "plan").filter(matchesView);
    case "personal":
      return items.filter((i) => i.source === "personal").filter(matchesView);
    case "coach":
      // A row assigned BY someone else: the fan-out already wrote it into
      // the caller's own partition (programsState/plansState), so this is
      // the same personal set as above, just discriminated by author.
      // `callerSub === null` ⇒ not yet known ⇒ NO match (see header).
      // ⭐ In the COACH view `matchesView` empties this branch outright:
      // assigned work lives in the athlete library. The chip that selects
      // this filter is hidden there for the same reason (LibraryBrowser),
      // so the coach never faces a tab that is empty by construction.
      return callerSub === null
        ? []
        : items
            .filter(
              (i) => i.source === "personal" && personalCreatedBy(i) !== callerSub,
            )
            .filter(matchesView);
  }
};

export type LibraryDifficulty = "beginner" | "intermediate" | "advanced";

export type LibraryTagFilters = {
  query: string;
  muscles: ReadonlySet<string>;
  equipment: ReadonlySet<EquipmentKind>;
  levels: ReadonlySet<LibraryDifficulty>;
  perWeek: ReadonlySet<number>;
};

export const emptyLibraryFilters: LibraryTagFilters = {
  query: "",
  muscles: new Set(),
  equipment: new Set(),
  levels: new Set(),
  perWeek: new Set(),
};

/** How many axes are active — the count on the "Filters" toggle. */
export const activeAxisCount = (f: LibraryTagFilters): number =>
  (f.muscles.size > 0 ? 1 : 0) +
  (f.equipment.size > 0 ? 1 : 0) +
  (f.levels.size > 0 ? 1 : 0) +
  (f.perWeek.size > 0 ? 1 : 0);

type LibraryItemTags = {
  muscles: string[];
  equipment: EquipmentKind[];
  difficulty?: LibraryDifficulty;
  perWeek?: number;
};

const equipmentOf = (
  slugs: ReadonlyArray<string>,
  catalogBySlug: ReadonlyMap<string, Exercise>,
): EquipmentKind[] => {
  const set = new Set<EquipmentKind>();
  for (const slug of slugs) {
    const kind = catalogBySlug.get(slug)?.equipment;
    if (kind) set.add(kind);
  }
  return Array.from(set);
};

export const libraryItemTags = (
  item: LibraryItem,
  catalogBySlug: ReadonlyMap<string, Exercise>,
): LibraryItemTags => {
  if (item.kind === "program") {
    const sessions =
      item.source === "personal" ? item.program.sessions : item.template.sessions;
    return {
      muscles: programMuscles(sessions, catalogBySlug),
      equipment: equipmentOf(programExerciseSlugs(sessions), catalogBySlug),
      ...(item.source === "official"
        ? { difficulty: item.template.difficulty }
        : {}),
      perWeek:
        item.source === "personal"
          ? item.program.sessionsPerWeek
          : item.template.sessionsPerWeek,
    };
  }
  const entries =
    item.source === "personal" ? item.plan.entries : item.template.entries;
  return {
    muscles: planMuscles(entries, catalogBySlug),
    equipment: equipmentOf(planExerciseSlugs(entries), catalogBySlug),
    ...(item.source === "official"
      ? { difficulty: item.template.difficulty }
      : {}),
  };
};

const intersects = <T>(
  active: ReadonlySet<T>,
  values: ReadonlyArray<T>,
): boolean => values.some((v) => active.has(v));

export const applyLibraryFilters = (
  items: ReadonlyArray<LibraryItem>,
  filters: LibraryTagFilters,
  catalogBySlug: ReadonlyMap<string, Exercise>,
  // PE-21 — the reader's language: search matches what they can SEE on
  // the card, not a language the resolution happened to pick.
  locale?: Locale,
): LibraryItem[] => {
  const q = normalizeSearch(filters.query.trim());
  return items.filter((item) => {
    if (q.length > 0) {
      const inName = normalizeSearch(libraryItemName(item, locale)).includes(q);
      const inDesc =
        item.source === "official" &&
        normalizeSearch(item.template.description).includes(q);
      if (!inName && !inDesc) return false;
    }
    if (
      filters.muscles.size === 0 &&
      filters.equipment.size === 0 &&
      filters.levels.size === 0 &&
      filters.perWeek.size === 0
    )
      return true;
    const tags = libraryItemTags(item, catalogBySlug);
    if (filters.muscles.size > 0 && !intersects(filters.muscles, tags.muscles))
      return false;
    if (
      filters.equipment.size > 0 &&
      !intersects(filters.equipment, tags.equipment)
    )
      return false;
    if (
      filters.levels.size > 0 &&
      (tags.difficulty === undefined || !filters.levels.has(tags.difficulty))
    )
      return false;
    if (
      filters.perWeek.size > 0 &&
      (tags.perWeek === undefined || !filters.perWeek.has(tags.perWeek))
    )
      return false;
    return true;
  });
};

export type LibraryFilterOptions = {
  muscles: string[];
  equipment: EquipmentKind[];
  levels: LibraryDifficulty[];
  perWeek: number[];
};

/** Options actually present across the whole item set — an axis chip you
 *  can select always matches at least one item. Unsorted except perWeek
 *  (numeric); muscle sort-by-localized-name is the caller's concern. */
export const collectLibraryFilterOptions = (
  items: ReadonlyArray<LibraryItem>,
  catalogBySlug: ReadonlyMap<string, Exercise>,
): LibraryFilterOptions => {
  const muscles = new Set<string>();
  const equipment = new Set<EquipmentKind>();
  const levels = new Set<LibraryDifficulty>();
  const perWeek = new Set<number>();
  for (const item of items) {
    const tags = libraryItemTags(item, catalogBySlug);
    for (const m of tags.muscles) muscles.add(m);
    for (const e of tags.equipment) equipment.add(e);
    if (tags.difficulty) levels.add(tags.difficulty);
    if (tags.perWeek !== undefined) perWeek.add(tags.perWeek);
  }
  const levelOrder: LibraryDifficulty[] = [
    "beginner",
    "intermediate",
    "advanced",
  ];
  return {
    muscles: Array.from(muscles),
    equipment: Array.from(equipment),
    levels: levelOrder.filter((l) => levels.has(l)),
    perWeek: Array.from(perWeek).sort((a, b) => a - b),
  };
};

// Moved from LibraryBrowser.tsx (500-line cap — CO-21) to sit next to the
// browser's other pure filter-tab logic.
export const LIBRARY_FILTERS: ReadonlyArray<LibraryFilter> = [
  "favorites",
  "programs",
  "workouts",
  "personal",
  "coach",
];

/** CO-19 — the "coach" chip means "assigned to me BY someone else", and in
 *  the coach view that set is empty by construction (assigned work lives in
 *  the athlete library — see filterLibraryItems' `matchesView`). Showing it
 *  there would also print the word "Coach" a third time on one screen, next
 *  to the coach Experience and the coach Library tab, with a third meaning.
 *  Found by jl-ux-a11y-auditor, 20/09. */
export const filtersForView = (
  authoredAs: Program["authoredAs"] | null | undefined,
): ReadonlyArray<LibraryFilter> =>
  authoredAs === "coach"
    ? LIBRARY_FILTERS.filter((f) => f !== "coach")
    : LIBRARY_FILTERS;
